import { Breadcrumb, Callout, Checkbox, CommandBarButton, DetailsListLayoutMode, DirectionalHint, GroupHeader, IColumn, IconButton, IGroup, Shimmer, ShimmerElementType, TooltipHost } from "@fluentui/react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CoalescedPod, Pod, PodAddProviderRequest, PodError, PodProvider } from "../../api/pods.types";
import { ROUTES } from "../../config/routes";
import { PageHeader } from "../core/PageHeader";
import styles from './PodPage.module.scss';
import { useBoolean, useId } from "@fluentui/react-hooks";
import useLocalStorage from "../../util/hooks/useLocalStorage";
import PodDetailsPanel, { PodDetailsPanelShimmer } from "./PodDetailsPanel";
import PodPersona from "./PodPersona";
import moment from "moment";
import PodShimmeredTable from "./PodShimmeredTable";
import usePodHistoryDialog from "./hooks/usePodHistoryDialog";
import PodCapacity from "./PodCapacity";
import { MAX_PATIENTS_BY_CATEGORY, MAX_POD_CLINICIANS, SUPPORT_STAFF_POSITIONS } from "../../config/constants";
import PodStatusBadge from "./PodStatusBadge";
import { coalesceAndSortProviders, getProviderCategoryByPosition } from "./Util";
import PodPlaceholder from "./PodPlaceholder";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../redux/message/actions';
import { ToastType } from "../../redux/message/types";
import PodStatusSpinner, { PodStatusSpinnerState } from "./PodStatusSpinner";
import useRemoveProviderDialog from "./hooks/useRemoveProviderDialog";
import useAddProviderDialog from "./hooks/useAddProviderDialog";
import { addPodProvider, getPodById, getPodsByProviderId, updatePod, updatePodProvider } from "../../api/pods";
import PodErrorBoundary from "./PodErrorBoundary";
import { PodStateContext } from "./state/reducer";
import { getUserState } from "./state/selectors";
import { RootState } from "../../redux/types";

interface PodBreadcrumbsProps {
  pod?: Pod;
  isLoading?: boolean;
  error?: PodError;
  canStepBack?: boolean;
  isReadonly?: boolean;
}

const PodBreadcrumbs = ({pod, isLoading, error, isReadonly, canStepBack}: PodBreadcrumbsProps) => {
  const navigate = useNavigate();
    
  return (
    <Breadcrumb
      className={styles.breadcrumbs}
      onReduceData={() => undefined}
      items={[
        {
          text: 'Pods',
          key: 'pods-search',
          href: ROUTES.pods,
          onRender: (props, _) => {
            if (!canStepBack) return <Link className={`title ${styles.breadcrumbLink}`} to={props?.href!}>{props?.text}</Link>;
            else return <Link className={`title ${styles.breadcrumbLink}`} to={''} onClick={() => navigate(-1)}>{props?.text}</Link>;
          }
        },
        {
          text: pod?.name ?? 'Pod',
          key: `pod-${pod?.id}`,
          href: '',
          onRender: (props, _) => {
            if (!!error) {
              return <div className={`title ${styles.breadcrumbLink}`}>
                {
                  error.status === 404 
                  ? 'Not Found'
                  : 'Error'
                }
              </div>;
            } else if (isLoading) {
              return (
                <div className={`title ${styles.breadcrumbLink}`}>
                  <Shimmer shimmerElements={[{type: ShimmerElementType.line, height: 24, width: 250}]}/>
                </div>
              );
            } else {
              return (
                <div className={`title ${styles.breadcrumbLink}`}>
                  {props?.text}
                  <div className={styles.badgeHolder}>
                    {pod?.status === 'DEACTIVATED' && (
                      <TooltipHost
                        content="This pod is inactive. Add a provider to reactivate it."
                        styles={{ root: { display: 'flex' } }}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                      >
                        <PodStatusBadge type='DEACTIVATED' />
                      </TooltipHost>
                    )}
                    {isReadonly && !error && (
                      <TooltipHost
                        content="You don’t have permission to edit this pod."
                        styles={{ root: { display: 'flex' } }}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                      >
                        <PodStatusBadge text='Read-only' />
                      </TooltipHost>
                    )}
                  </div>
                </div>
              )
            }
          }
        }
      ]}
    />
  )
};

const locale = Intl.DateTimeFormat()?.resolvedOptions()?.locale ?? 'en-US';
const relFormat = new Intl.RelativeTimeFormat(locale);
const absFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short' });

const PodPage = () => {
  const { id: podId } = useParams();
  const filterButtonId = useId();
  const dispatch = useDispatch();
  const optionKeyDepressed = useSelector((state: RootState) => state.ui.optionKeyDepressed);

  const {useContextSelector} = useContext(PodStateContext);
  const userIsOpsManager = useContextSelector(getUserState).isManager;

  const sendToast = (type: ToastType, title: string, content?: string) => dispatch(toast({ type, title, content }));

  const locationState = useLocation().state as {canStepBackToSearch: boolean} | undefined;
  const canStepBackToSearch = useMemo(() => !!locationState?.canStepBackToSearch, [locationState]);

  const [podData, setPodData] = useState<CoalescedPod>();
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarSavingState, setSidebarSavingState] = useState<PodStatusSpinnerState>();

  const providersBeingRemoved = useRef(new Set());

  const [loadingError, setLoadingError] = useState<PodError>();

  const [filterShowDeactivated, setFilterShowDeactivated] = useLocalStorage('pods-showDeactivatedProviders', false);
  const [filterCalloutVisible, {toggle: setFilterCalloutVisible}] = useBoolean(false);

  const [
    podHistoryDialog,
    showHistoryDialog,
  ] = usePodHistoryDialog(podData);

  const fetchPod = useCallback((id?: string) => {
    if (id) {
      setIsLoading(true);
      getPodById(id)
        .then(async (pod) => {
          const coalescedAndSorted = await coalesceAndSortProviders(pod);
          setPodData(coalescedAndSorted);
          setIsLoading(false);
        })
        .catch(error => {
          if (error instanceof Error) {
            setLoadingError({
              message: error.message,
              diagnostic: error.stack,
            });
          } else {
            setLoadingError(error);
          }
          setIsLoading(false);
        });
    }
  }, []);

  const handleConfirmedRemove = useCallback((id: string, name: string) => {
    if (!podData) return;

    if (!userIsOpsManager) {
      sendToast('error', 'Not allowed', `You are not allowed to remove providers.`);
      hideRemoveConfirmation();
      return;
    }

    providersBeingRemoved.current.add(id);

    // Call API
    setRemoveConfirmationIsLoading(true);
    updatePodProvider(podData.id, id, {
      status: 'DEACTIVATED',
    })
      .then(() => {
        sendToast('success', 'Provider removed', `${name} has been removed.`);
        fetchPod(podId);
      })
      .catch(error => {
        sendToast('error', 'Unable to remove', `Could not remove ${name}. Please try again later.`);
      })
      .finally(() => {
        hideRemoveConfirmation();
        providersBeingRemoved.current.delete(id);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPod, podData, podId, sendToast, userIsOpsManager]);

  const [
    removeProviderDialog,
    showRemoveConfirmation,
    hideRemoveConfirmation,
    setRemoveConfirmationIsLoading,
  ] = useRemoveProviderDialog(podData, handleConfirmedRemove);

  const handleAddProvider = async (data: PodAddProviderRequest, skipAssignmentCheck?: boolean) => {
    if (!podData) return;

    if (!userIsOpsManager) {
      sendToast('error', 'Not allowed', `You are not allowed to add providers.`);
      hideAddProviderDialog();
      return;
    }

    setAddProviderIsLoading(true);

    if (!skipAssignmentCheck) {
      const existingPods = await getPodsByProviderId(data.providerId)
        .then(pods => {
          return pods;
        })
        .catch(error => {
          console.log(error);
          return undefined;
        });

      if (existingPods === undefined) {
        setAddProviderError('Unable to add the provider. Please try again.');
        return;
      }

      if (existingPods.length > 0) {
        setAddProviderError('This provider is already assigned to a pod.');
        return;
      }
    }

    addPodProvider(podData.id, data)
      .then(provider => {
        const fullName = `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`
        sendToast('success', 'Provider added', `${fullName} has been added.`);
        hideAddProviderDialog();
        fetchPod(podId);
      })
      .catch(error => {
        setAddProviderError('Unable to add the provider. Please try again.');
      });
  }

  const [
    addProviderDialog,
    showAddProviderDialog,
    hideAddProviderDialog,
    setAddProviderIsLoading,
    setAddProviderError
  ] = useAddProviderDialog(podData, handleAddProvider);

  const handleSidebarSave = (name: string, description: string) => {
    if (!podData) return;

    if (!userIsOpsManager) {
      setSidebarSavingState(undefined);
      sendToast('error', 'Not Allowed', `You are not allowed to edit pod details.`);
      setPodData(podData => ({ ...podData! }));
      return;
    }

    setSidebarSavingState(PodStatusSpinnerState.loading);

    updatePod(podData.id, {
      name,
      description
    })
      .then(pod => {
        sendToast('success', 'Save successful', 'All changes have been saved');
        setSidebarSavingState(PodStatusSpinnerState.success);

        /**
         * If saving is successful, update the pod data.
         * This will trickle down into the side panel and remove the "unsaved" indicators.
         * If unsuccessful, skip this step so the unsaved changes are preserved.
         */

        setPodData(prevPod => ({
          ...prevPod!, // Necessarily defined because of the guard at the top of handleSidebarSave
          name: pod.name,
          description: pod.description
        }));
      })
      .catch(error => {
        console.log(error);
        sendToast('error', 'Save failed', 'Unable to save changes');
        setSidebarSavingState(PodStatusSpinnerState.failure);
      })
  }

  const columns = useMemo<IColumn[]>(() => {
    return [
      {
        key: 'provider',
        name: 'Provider',
        minWidth: 250,
        // maxWidth: 400,
        flexGrow: 5,
        onRender: (provider: PodProvider) => <PodPersona provider={provider} inactive={provider.status === 'DEACTIVATED'} />,
      },
      {
        key: 'position',
        name: 'Position',
        fieldName: 'position',
        minWidth: 200,
        // maxWidth: 280,
        flexGrow: 4,
        onRender: (provider: PodProvider) => (
          provider.status === 'DEACTIVATED'
            ? <span className={styles.inactive}>{provider.position}</span>
            : provider.position
        )
      },
      {
        key: 'patients',
        name: 'Patients',
        minWidth: 160,
        // maxWidth: 180,
        flexGrow: 3,
        onRender: (provider: PodProvider) => {
          if (provider.providerId === null || provider.status === 'DEACTIVATED') return null;
          const category = getProviderCategoryByPosition(provider) ?? 'Support Staff';

          /**
           * Note: If position isn't returned by the API, all providers will be deemed to be
           * Support Staff here and their patient counts will be blank in the table. This is a
           * fail condition, and thus errs on the side of not displaying maxima that may be
           * incorrect, rather than trying to display based on the suffix, which, in rare,
           * hypothetical edge cases, may not match the current position.
           */
          if (category === 'Support Staff' && provider.position !== 'Patient Care Coordinator') return null;

          const max = MAX_PATIENTS_BY_CATEGORY[category];
          return <PodCapacity count={provider.patientCount ?? 0} max={max}/>
        }
      },
      {
        key: 'updated',
        name: !!filterShowDeactivated ? 'Updated' : 'Added',
        minWidth: 150,
        flexGrow: 3,
        onRender: (provider: PodProvider) => {
          if (provider.providerId === null) return null;

          const evt = (provider.status === 'ACTIVATED' ? provider.activatedAt : provider.deactivatedAt!) * 1000;
          const diff = moment(new Date(evt)).diff(moment(), 'milliseconds');
          const duration = moment.duration(diff);

          const segments = [duration.years(), duration.months(), duration.weeks(), duration.days(), duration.hours(), duration.minutes()];
          const types: Intl.RelativeTimeFormatUnit[] = ['years', 'months', 'weeks', 'days', 'hours', 'minutes'];

          let relStr = 'Just now';

          for (let i = 0; i < segments.length; i++) {
            if (segments[i] !== 0) {
              relStr = relFormat.format(segments[i], types[i]);
              break;
            }
          }

          const absStr = absFormat.format(new Date(evt));

          return (
            <TooltipHost content={absStr}>
              {provider.status === 'DEACTIVATED'
                ? <span className={styles.inactive}>{relStr}</span>
                : relStr
              }
              
            </TooltipHost>
          );
        }
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 100,
        flexGrow: 2,
        onRender: (provider: PodProvider) => {
          if (provider.providerId === null) return null;
          const isActive = provider.status === 'ACTIVATED';
          
          let removeTooltipText = !optionKeyDepressed
            ? 'Remove from pod'
            : 'Force remove from pod';

          if (!userIsOpsManager) removeTooltipText = 'Removing providers is disabled in read-only mode.';
          else if (!isActive) removeTooltipText = 'Already removed from pod';

          const providerIsBeingRemoved = providersBeingRemoved.current.has(provider.id);
          if (providerIsBeingRemoved) removeTooltipText = 'Already removing...';

          return (
            <div className={styles.rowActions}>
              <TooltipHost content="View profile">
                <Link to={`${ROUTES.providers}/${provider.providerId}`} tabIndex={-1} data-is-focusable={false}>
                  <IconButton
                    className={styles.rowAction}
                    iconProps={{ iconName: 'ContactCard' }}
                    ariaLabel="View profile"
                  />
                </Link>
              </TooltipHost>
              <TooltipHost content='Show history'>
                <IconButton
                  className={styles.rowAction}
                  iconProps={{ iconName: 'History' }}
                  onClick={() => showHistoryDialog(provider.providerId)}
                  ariaLabel="Show history"
                />
              </TooltipHost>
              <TooltipHost
                content={removeTooltipText}
                directionalHint={DirectionalHint.topRightEdge}
              >
                <IconButton
                  className={`${styles.rowAction}${optionKeyDepressed ? ` ${styles.danger}` : ''}`}
                  disabled={!isActive || !userIsOpsManager || providerIsBeingRemoved}
                  iconProps={{ iconName: 'Trash' }}
                  onRenderIcon={(props, defaultRender) => {
                    if (providerIsBeingRemoved) return <PodStatusSpinner state={PodStatusSpinnerState.loading}/>;
                    else return defaultRender!(props);
                  }}
                  onClick={() => {
                    /**
                     * If the user option-clicks the button, immediately remove the provider.
                     * Otherwise, show a dialog to confirm the operation. The optionKeyDepressed
                     * boolean has a tendency to be a bit sticky, especially when changing focus
                     * between elements (e.g., holding option and focusing a input, then letting
                     * go). However, it needs to be used here since the button styling uses it for
                     * its global state, and using e.altKey could result in a discontinuity where
                     * the button says "Force remove..." but clicking pops the dialog (or worse,
                     * the opposite). Having both things potentially be sticky is better than
                     * having only one of them be this way and misleading the user.
                     */
                    if (!optionKeyDepressed) {
                      showRemoveConfirmation(provider.id);
                    } else {
                      handleConfirmedRemove(provider.id, `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`);
                    }
                  }}
                  ariaLabel={removeTooltipText}
                />
              </TooltipHost>
            </div>
          )
        }
      },
    ];
  }, [filterShowDeactivated, showHistoryDialog, showRemoveConfirmation, handleConfirmedRemove, userIsOpsManager, optionKeyDepressed]);

  /**
   * Run through the list, finding and isolating each type of provider:
   *   - Lead Physician
   *   - Clinicians
   *   - Support Staff
   * 
   * Combine the providers into one list, then generate group data to match.
   * 
   */
  const [providerList, groupList, activeClinicians]: [PodProvider[], IGroup[], number] = useMemo(() => {
    if (isLoading) {
      return [Array(7).fill({}), [
        { key: 'physician', name: 'Physician', startIndex: 0, count: 1, level: 0 },
        { key: 'clinicians', name: `Clinicians`, startIndex: 1, count: 4, level: 0 },
        { key: 'supportStaff', name: 'Support Staff', startIndex: 5, count: 2, level: 0 },
      ], 0];
    }

    if (!podData) return [[], [
      { key: 'physician', name: 'Physician', startIndex: 0, count: 0, level: 0 },
      { key: 'clinicians', name: `Clinicians`, startIndex: 0, count: 0, level: 0 },
      { key: 'supportStaff', name: 'Support Staff', startIndex: 0, count: 0, level: 0 }
    ], 0];

    const physician = [];
    const clinicians = [];
    const supportStaff = [];

    let totalPatientLoad = 0;
    let activeClinicians = 0;

    for (const provider of podData.providers) {
      if (!filterShowDeactivated && provider.status === 'DEACTIVATED') {
        continue;
      }

      if (provider.position === 'Physician' || provider.id === podData.leadProviderId) {
        physician.push(provider);
        if (provider.status === 'ACTIVATED') {
          totalPatientLoad += provider.patientCount;
        }
      } else if (SUPPORT_STAFF_POSITIONS.includes(provider.position)) {
        supportStaff.push(provider);
      } else {
        clinicians.push(provider);
        if (provider.status === 'ACTIVATED') {
          totalPatientLoad += provider.patientCount;
          activeClinicians += 1;
        }
      }
    }

    let lastPCCIndex = -1;

    for (let i = 0; i < supportStaff.length; i++) {
      if (supportStaff[i].position !== 'Patient Care Coordinator' || supportStaff[i].status !== 'ACTIVATED') continue;

      lastPCCIndex = i;
      const patientCountToAssign = Math.min(totalPatientLoad, MAX_PATIENTS_BY_CATEGORY["Support Staff"]);

      supportStaff[i].patientCount = patientCountToAssign;

      totalPatientLoad -= patientCountToAssign;
    }

    if (totalPatientLoad > 0 && lastPCCIndex > -1) {
      supportStaff[lastPCCIndex].patientCount += totalPatientLoad;
    }

    return [
      [...physician, ...clinicians, ...supportStaff],
      [
        { key: 'physician', name: 'Physician', startIndex: 0, count: physician.length, level: 0 },
        { key: 'clinicians', name: `Clinicians`, startIndex: physician.length, count: clinicians.length, level: 0 },
        { key: 'supportStaff', name: 'Support Staff', startIndex: physician.length + clinicians.length, count: supportStaff.length, level: 0 },
      ],
      activeClinicians
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podData, filterShowDeactivated, isLoading]);

  /**
   * Fetch the pod data on initial render.
   * The podId is derived from the URL, so it won't change without triggering a full reload.
   */
  useEffect(() => fetchPod(podId), [podId, fetchPod]);

  const errorTitle = loadingError
    ? loadingError.status === 404
      ? 'Not Found'
      : 'Error'
    : undefined;

  const pageTitle = podData
    ? podData.name
    : loadingError
      ? errorTitle
      : 'Pods';

  return (
    <main className={`${styles.podsContainer} ${!loadingError ? styles.hasSidebar : ''}`}>
      <PageHeader
        documentTitle={pageTitle}
        title={<PodBreadcrumbs
          pod={!isLoading ? podData : undefined}
          isLoading={isLoading}
          error={loadingError}
          canStepBack={canStepBackToSearch}
          isReadonly={!userIsOpsManager}
        />}
        actions={{
          items: [
            {
              key: 'addMember',
              text: 'Add provider',
              iconProps: { iconName: 'AddFriend' },
              disabled: isLoading || !!loadingError || !userIsOpsManager,
              onRender: (props) => (
                <CommandBarButton {...props} onRenderText={(props, defaultRender) => (
                  <TooltipHost
                    content={!userIsOpsManager ? 'Adding providers is disabled in read-only mode.' : ''}
                    styles={{
                      root: {
                        display: 'flex',
                      }
                    }}
                    directionalHint={DirectionalHint.bottomRightEdge}
                  >   
                    {defaultRender!(props)}
                  </TooltipHost>
                )} />
              ),
              onClick: () => {
                showAddProviderDialog();
              }
            },
            {
              key: 'filterSettings',
              onRender: () => {
                return (<>
                  <CommandBarButton
                    id={filterButtonId}
                    iconProps={{ iconName: 'Filter' }}
                    onClick={setFilterCalloutVisible}
                    disabled={isLoading || !!loadingError}
                    ariaLabel="Show filters"
                  />
                  {filterCalloutVisible && (
                    <Callout
                      role='dialog'
                      directionalHint={DirectionalHint.bottomRightEdge}
                      onDismiss={setFilterCalloutVisible}
                      target={`#${filterButtonId}`}
                      className={styles.filterCallout}
                      setInitialFocus
                    >
                      <Checkbox
                        label='Show removed'
                        checked={filterShowDeactivated}
                        onChange={(_, checked) => setFilterShowDeactivated(!!checked)}
                        boxSide='end'
                        onRenderLabel={(props) => <span className={styles.filterCheckboxLabel}>{props?.label}</span>}
                        inputProps={{
                          tabIndex: 0,
                        }}
                      />
                    </Callout>
                  )}
                </>);
              },
            }
          ],
        }}
      />

      {addProviderDialog}

      {podHistoryDialog}

      {removeProviderDialog}

      <PodErrorBoundary>
        <div className={styles.tableArea}>
          <PodShimmeredTable
            columns={columns}
            items={providerList}
            layoutMode={DetailsListLayoutMode.justified}
            groups={groupList}
            groupProps={{
              showEmptyGroups: providerList.length > 0,
              headerProps: {
                expandButtonProps: {
                  hidden: true,
                },
              },
              onRenderHeader: (props) => (
                <GroupHeader
                  {...props}
                  onRenderTitle={(props) => {
                    const showCapacity = !isLoading && props?.group?.key === 'clinicians';
                    return <div className={`${styles.groupHeader}${!props?.group?.isCollapsed ? ` ${styles.expanded}` : ''}`}>
                      {props?.group?.name}
                      {showCapacity && (
                        <TooltipHost
                          content={`${activeClinicians} of ${MAX_POD_CLINICIANS} slots occupied`}
                          directionalHint={DirectionalHint.topLeftEdge}
                        >
                          <div
                            className={`${styles.groupCapacity}${activeClinicians >= MAX_POD_CLINICIANS ? ` ${styles.full}` : ''}`}
                            aria-label={`; ${activeClinicians} of ${MAX_POD_CLINICIANS} slots occupied`}
                          >
                            <span aria-hidden={true}>
                              {activeClinicians}/{MAX_POD_CLINICIANS}
                            </span>
                          </div>
                        </TooltipHost>
                      )}
                    </div>
                  }}
                />
              ),
            }}
            shimmerLines={7}
            enableShimmer={isLoading}
            useInactiveStyle={true}
            onRenderEmptyState={() => {
              return <PodPlaceholder iconName="BusinessHoursSign" text="This pod has no active providers." action={{
                iconName: 'Filter',
                text: 'Show removed',
                onClick: () => setFilterShowDeactivated(true),
              }} />;
            }}
            renderPlaceholder={!loadingError ? undefined : () => {
              if (loadingError.status === 404) {
                return <PodPlaceholder iconName="WebAppBuilderModule" text="The specified pod does not exist." />
              } else {
                return <PodPlaceholder type="error" iconName="Cancel" text="An error occurred while loading the pod." output={loadingError.diagnostic ?? loadingError.message} />;
              }
            }}
          />
        </div>

        {/**
          * DO NOT use PageSidePanel for this. Doing so seems to cause a minute delay between
          * the initial render and the panel appearing, which in turn causes the DetailsList to
          * be sized incorrectly. What's more, because this delay also occurs when the panel
          * disappears, navigating back to the main Pods search page will cause THAT page's table
          * to have the width that this table SHOULD have had, since the PageSidePanel from this
          * page was visible when that page started rendering. I tried a variety of things here,
          * but nothing beat the simplicity and reliability of just manually positioning a div.
          */}
        { isLoading
          ? <PodDetailsPanelShimmer />
          : <PodDetailsPanel pod={!loadingError ? podData : undefined} savingState={sidebarSavingState} onSave={handleSidebarSave} />
        }
      </PodErrorBoundary>

    </main>
  )
}

export default PodPage;